import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  IconButton,
  Box,
  useTheme,
  Typography,
  TableRow,
  Grid,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  TABLE_HEAD_PORTFOLIO_SHORT,
  TABLE_HEAD_PORTFOLIO_LONG,
  applySortFilter,
  getComparator,
} from "../../../component/Methods";
import PositionPopUpV2 from "./PositionPopUpV2";
import PositionTableV2 from "./PositionTableV2";
import PositionTableHeadV2 from "./PositionTableHeadV2";
import {
  V2_POSITION_ROW_HEIGHT,
  V2_POSITION_VIRTUAL_BUFFER,
  getV2PositionTableBodyHeight,
  getTableShellSx,
  getSideHeaderSx,
} from "./positionTableV2Styles";
import { v2LabelSx } from "../../PerformanceDashboardV2/v2Theme";
import {
  useViewportBoundedHeight,
  V2_POSITION_PANEL_HEADER_HEIGHT,
} from "../hooks/useViewportBoundedHeight";

const ROW_HEIGHT = V2_POSITION_ROW_HEIGHT;
const BUFFER_SIZE = V2_POSITION_VIRTUAL_BUFFER;

const SLICE_HEADERS = (headers) => [...headers.slice(0, 8), ...headers.slice(10)];

const PositionsPanel = ({
  side,
  title,
  count,
  columns,
  tickerColumnId,
  virtualData,
  totalRows,
  tableRef,
  panelRef,
  onScroll,
  order,
  orderBy,
  onRequestSort,
  onExpand,
  fundVal,
  strategyVal,
  tableScrollHeight,
}) => {
  const theme = useTheme();
  const isLong = side === "long";

  return (
    <Box ref={panelRef} sx={{ ...getTableShellSx(theme), width: "100%" }}>
      <Box sx={getSideHeaderSx(theme, side)}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.04em",
            fontSize: "0.95rem",
            ...v2LabelSx(theme),
          }}
        >
          {title} ({count})
        </Typography>
        <IconButton
          onClick={onExpand}
          aria-label={`Expand ${title}`}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <FullscreenIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <TableContainer
        onScroll={onScroll}
        ref={tableRef}
        sx={{
          overflow: "auto",
          ...(tableScrollHeight
            ? {
                height: tableScrollHeight,
                maxHeight: tableScrollHeight,
              }
            : null),
        }}
      >
        <Table size="small" stickyHeader sx={{ width: "100%" }}>
          <PositionTableHeadV2
            columns={columns}
            order={order}
            orderBy={orderBy}
            onRequestSort={onRequestSort}
            tickerColumnId={tickerColumnId}
          />
          <TableBody>
            {virtualData.startIndex > 0 && (
              <TableRow
                style={{ height: virtualData.startIndex * ROW_HEIGHT }}
              >
                <td colSpan={columns.length} />
              </TableRow>
            )}

            {virtualData.visibleRows?.map((row) => (
              <PositionTableV2
                key={`${row[tickerColumnId]}-${row.virtualIndex}`}
                row={row}
                fundVal={fundVal}
                strategyVal={strategyVal}
                positionType={isLong ? "long" : "short"}
              />
            ))}

            {virtualData.endIndex < totalRows && (
              <TableRow
                style={{
                  height: (totalRows - virtualData.endIndex) * ROW_HEIGHT,
                }}
              >
                <td colSpan={columns.length} />
              </TableRow>
            )}

            {!totalRows && (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ border: "none" }}>
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No {isLong ? "long" : "short"} positions
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const AssetPositionsV2 = React.memo(({ data, fundVal, StrategeyVal, onExportDataChange }) => {
  const [longPanelEl, setLongPanelEl] = useState(null);
  const viewportMaxHeight = useViewportBoundedHeight(longPanelEl);
  const tableScrollHeight = viewportMaxHeight
    ? Math.max(160, viewportMaxHeight - V2_POSITION_PANEL_HEADER_HEIGHT)
    : null;

  const [long, setlong] = useState([]);
  const [expandLong, setExpandLong] = useState([]);
  const [short, setshort] = useState([]);
  const [expandShort, setExpandShort] = useState([]);
  const [orderByShort, setOrderByShort] = useState("short_size");
  const [orderByLong, setOrderByLong] = useState("long_size");
  const [longSortOrder, setLongSortOrder] = useState("desc");
  const [longFilterName, setLongFilterName] = useState("");
  const [shortSortOrder, setShortSortOrder] = useState("desc");
  const [shortFilterName, setShortFilterName] = useState("");
  const [isGridView, setIsGridView] = useState(false);
  const [isGridViewShort, setIsGridViewShort] = useState(false);

  const longTableRef = useRef(null);
  const shortTableRef = useRef(null);
  const [longScrollTop, setLongScrollTop] = useState(0);
  const [shortScrollTop, setShortScrollTop] = useState(0);
  const [longContainerHeight, setLongContainerHeight] = useState(0);
  const [shortContainerHeight, setShortContainerHeight] = useState(0);

  const longColumns = useMemo(
    () => SLICE_HEADERS(TABLE_HEAD_PORTFOLIO_LONG),
    []
  );
  const shortColumns = useMemo(
    () => SLICE_HEADERS(TABLE_HEAD_PORTFOLIO_SHORT),
    []
  );

  const measureContainer = useCallback((node, setHeight) => {
    if (!node) return undefined;
    const updateHeight = () => setHeight(node.clientHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    return measureContainer(longTableRef.current, setLongContainerHeight);
  }, [long.length, measureContainer]);

  useLayoutEffect(() => {
    return measureContainer(shortTableRef.current, setShortContainerHeight);
  }, [short.length, measureContainer]);

  const processedData = useMemo(() => {
    const todayShares = (item) => Number(item?.deltaAdjustedShareCount);
    const overnightShares = (item) =>
      Number(
        item?.deltaAdjustedShareCountWithoutTrades ??
          item?.deltaAdjustedShareCount
      );
    const isLongSide = (item) => {
      const today = todayShares(item);
      if (today > 0) return true;
      if (today < 0) return false;
      return overnightShares(item) > 0;
    };
    const longItems = data?.filter((i) => isLongSide(i));
    const shortItems = data?.filter((i) => !isLongSide(i));

    const renamedShort = shortItems
      ?.map((item) => ({
        short_size: item?.size,
        short_sizeWithoutTrades: item?.sizeWithoutTrades,
        short_current: item?.sharePrice,
        short_deltaadjustedsharecount: item?.deltaAdjustedShareCount,
        short_deltaadjustedsharecountWithoutTrades:
          item?.deltaAdjustedShareCountWithoutTrades,
        short_ch: item?.ch,
        short_pL: parseFloat(item?.pL),
        short_pLWithoutTrades: parseFloat(item?.pLWithoutTrades),
        short_cost: parseFloat(item?.avgCost),
        short_costWithoutTrades: parseFloat(item?.avgCostWithoutTrades),
        short_last: item?.last,
        short_tickerSymbol: item?.tickerSymbol,
        short_chpercentage: parseFloat(item?.chPercetage ?? item?.chPercentage),
        short_pl_avg_cost: parseFloat(
          (item?.last - item?.avgCost) * item?.deltaAdjustedShareCount
        ),
        short_percentage_avg_cost: parseFloat(
          ((item?.pL * item?.avgCost) / item?.avgCost) * 100
        ),
        dataSource: item?.dataSource,
        fetched: item?.fetched,
        valid: item?.valid,
        short_tdg: item?.tdg,
        short_custodian: item?.custodian,
      }))
      ?.sort((a, b) => b?.short_size - a?.short_size);

    const renamedLong = longItems
      ?.map((item) => ({
        long_size: item?.size,
        long_sizeWithoutTrades: item?.sizeWithoutTrades,
        long_deltaadjustedsharecount: item?.deltaAdjustedShareCount,
        long_deltaadjustedsharecountWithoutTrades:
          item?.deltaAdjustedShareCountWithoutTrades,
        long_ch: item?.ch,
        long_pL: parseFloat(item?.pL),
        long_pLWithoutTrades: parseFloat(item?.pLWithoutTrades),
        long_cost: parseFloat(item?.avgCost),
        long_costWithoutTrades: parseFloat(item?.avgCostWithoutTrades),
        long_last: item?.last,
        long_current: item?.sharePrice,
        long_tickerSymbol: item?.tickerSymbol,
        long_chpercentage: parseFloat(item?.chPercetage ?? item?.chPercentage),
        long_pl_avg_cost: parseFloat(
          (item?.last - item?.avgCost) * item?.deltaAdjustedShareCount
        ),
        long_percentage_avg_cost: parseFloat(
          ((item?.pL * item?.avgCost) / item?.avgCost) * 100
        ),
        dataSource: item?.dataSource,
        fetched: item?.fetched,
        valid: item?.valid,
        long_tdg: item?.tdg,
        long_custodian: item?.custodian,
      }))
      ?.sort((a, b) => b?.long_size - a?.long_size);

    return { renamedLong, renamedShort };
  }, [data]);

  useEffect(() => {
    const { renamedLong, renamedShort } = processedData;

    setExpandLong(renamedLong);
    setExpandShort(renamedShort);

    setlong(
      applySortFilter(
        renamedLong,
        getComparator(longSortOrder, orderByLong),
        longFilterName
      )
    );
    setshort(
      applySortFilter(
        renamedShort,
        getComparator(shortSortOrder, orderByShort),
        shortFilterName
      )
    );
    setLongScrollTop(0);
    setShortScrollTop(0);
  }, [
    processedData,
    longSortOrder,
    orderByLong,
    orderByShort,
    longFilterName,
    shortSortOrder,
    shortFilterName,
  ]);

  useEffect(() => {
    onExportDataChange?.({ long, short });
  }, [long, short, onExportDataChange]);

  const longVirtualData = useMemo(() => {
    if (!long?.length) {
      return { visibleRows: [], totalHeight: 0, startIndex: 0, endIndex: 0 };
    }

    const bodyHeight = getV2PositionTableBodyHeight(longContainerHeight);
    const startIndex = Math.max(
      0,
      Math.floor(longScrollTop / ROW_HEIGHT) - BUFFER_SIZE
    );
    const rowsInView = Math.ceil(bodyHeight / ROW_HEIGHT);
    const endIndex = Math.min(long.length, startIndex + rowsInView + BUFFER_SIZE * 2);

    return {
      visibleRows: long.slice(startIndex, endIndex).map((row, index) => ({
        ...row,
        virtualIndex: startIndex + index,
      })),
      totalHeight: long.length * ROW_HEIGHT,
      startIndex,
      endIndex,
    };
  }, [long, longScrollTop, longContainerHeight]);

  const shortVirtualData = useMemo(() => {
    if (!short?.length) {
      return { visibleRows: [], totalHeight: 0, startIndex: 0, endIndex: 0 };
    }

    const bodyHeight = getV2PositionTableBodyHeight(shortContainerHeight);
    const startIndex = Math.max(
      0,
      Math.floor(shortScrollTop / ROW_HEIGHT) - BUFFER_SIZE
    );
    const rowsInView = Math.ceil(bodyHeight / ROW_HEIGHT);
    const endIndex = Math.min(
      short.length,
      startIndex + rowsInView + BUFFER_SIZE * 2
    );

    return {
      visibleRows: short.slice(startIndex, endIndex).map((row, index) => ({
        ...row,
        virtualIndex: startIndex + index,
      })),
      totalHeight: short.length * ROW_HEIGHT,
      startIndex,
      endIndex,
    };
  }, [short, shortScrollTop, shortContainerHeight]);

  const handleLongScroll = useCallback((event) => {
    setLongScrollTop(event.target.scrollTop);
  }, []);

  const handleShortScroll = useCallback((event) => {
    setShortScrollTop(event.target.scrollTop);
  }, []);

  const handleRequestSort = useCallback(
    (event, property) => {
      if (property.startsWith("long_")) {
        setLongSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        setOrderByLong(property);
      } else if (property.startsWith("short_")) {
        setShortSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        setOrderByShort(property);
      }
    },
    []
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PositionsPanel
            side="long"
            title="Long Positions"
            count={long.length}
            columns={longColumns}
            tickerColumnId="long_tickerSymbol"
            virtualData={longVirtualData}
            totalRows={long.length}
            tableRef={longTableRef}
            panelRef={setLongPanelEl}
            onScroll={handleLongScroll}
            order={longSortOrder}
            orderBy={orderByLong}
            onRequestSort={handleRequestSort}
            onExpand={() => setIsGridView(true)}
            fundVal={fundVal}
            strategyVal={StrategeyVal}
            tableScrollHeight={tableScrollHeight}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PositionsPanel
            side="short"
            title="Short Positions"
            count={short.length}
            columns={shortColumns}
            tickerColumnId="short_tickerSymbol"
            virtualData={shortVirtualData}
            totalRows={short.length}
            tableRef={shortTableRef}
            onScroll={handleShortScroll}
            order={shortSortOrder}
            orderBy={orderByShort}
            onRequestSort={handleRequestSort}
            onExpand={() => setIsGridViewShort(true)}
            fundVal={fundVal}
            strategyVal={StrategeyVal}
            tableScrollHeight={tableScrollHeight}
          />
        </Grid>
      </Grid>

      <PositionPopUpV2
        open={isGridView}
        setOpen={setIsGridView}
        data={expandLong}
        positionType="long"
        title="Long Positions"
        fundVal={fundVal}
        strategyVal={StrategeyVal}
      />
      <PositionPopUpV2
        open={isGridViewShort}
        setOpen={setIsGridViewShort}
        data={expandShort}
        positionType="short"
        title="Short Positions"
        fundVal={fundVal}
        strategyVal={StrategeyVal}
      />
    </>
  );
});

AssetPositionsV2.displayName = "AssetPositionsV2";

export default AssetPositionsV2;
